// rich-text.ts — Assainissement du HTML riche au rendu (allow-list).
//
// Deuxième barrière, côté client. La première est serveur
// (`app/services/rich_text.py`, mêmes balises et mêmes attributs) : c'est elle
// qui protège vraiment, puisqu'un attaquant peut appeler l'API directement.
// Celle-ci couvre le cas d'un contenu déjà en base écrit avant l'assainisseur
// serveur, et évite d'injecter du HTML non filtré via `innerHTML`.
//
// Implémentation par parcours de l'arbre plutôt que par expression régulière :
// on laisse le parseur du navigateur normaliser, puis on élague. Une regex sur
// du HTML est un nid à contournements.

/** Balises conservées. Tout le reste est remplacé par son contenu textuel.
 * `B`/`I` en plus de `STRONG`/`EM` : `execCommand('bold'|'italic')` produit
 * `<b>`/`<i>` dans Chromium (vérifié empiriquement), pas `<strong>`/`<em>`.
 * Même risque nul (aucun attribut autorisé dessus). */
export const ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'UL',
  'OL',
  'LI',
  'STRONG',
  'EM',
  'B',
  'I',
  'U',
  'A',
]);

/** Attributs conservés, par balise. Tout le reste saute — dont les `on*`. */
const ALLOWED_ATTRS: Record<string, Set<string>> = { A: new Set(['href']) };

/**
 * Balises supprimées AVEC leur contenu. Les autres balises non autorisées sont
 * simplement déballées (on garde leur texte, cas d'un `<div>`/`<span>` issu
 * d'un copier-coller). Ici le contenu n'est pas du texte utilisateur mais du
 * code : le déballer laisserait `alert(1)` en clair dans l'ordre du jour.
 */
const DROP_WITH_CONTENT = new Set([
  'SCRIPT',
  'STYLE',
  'TEMPLATE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'NOSCRIPT',
  'TITLE',
  'HEAD',
]);

/** Protocoles autorisés dans un `href`. */
const ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:'];

/** Espace insécable, ce que devient `&nbsp;` une fois parsé. */
const NBSP = ' ';

function isSafeHref(value: string): boolean {
  try {
    // `base` arbitraire : on ne s'intéresse qu'au protocole résolu, ce qui
    // neutralise `javascript:` et `data:` y compris avec espaces ou casse
    // mélangée.
    const url = new URL(value, 'https://ornexia.invalid/');
    return ALLOWED_SCHEMES.includes(url.protocol);
  } catch {
    return false;
  }
}

function cleanElement(el: Element): void {
  for (const attr of Array.from(el.attributes)) {
    const allowed = ALLOWED_ATTRS[el.tagName]?.has(attr.name.toLowerCase());
    if (!allowed) {
      el.removeAttribute(attr.name);
      continue;
    }
    if (attr.name.toLowerCase() === 'href' && !isSafeHref(attr.value)) {
      el.removeAttribute(attr.name);
    }
  }
  if (el.tagName === 'A') {
    el.setAttribute('rel', 'noopener noreferrer');
    el.setAttribute('target', '_blank');
  }
}

function walk(node: Node): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.parentNode?.removeChild(child);
      continue;
    }
    const el = child as Element;
    if (DROP_WITH_CONTENT.has(el.tagName)) {
      el.parentNode?.removeChild(el);
      continue;
    }
    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Balise interdite : on la remplace par ses enfants (on ne perd pas le
      // texte d'un `<div>` ou d'un `<span>` produit par un copier-coller).
      walk(el);
      const parent = el.parentNode;
      while (el.firstChild) parent?.insertBefore(el.firstChild, el);
      parent?.removeChild(el);
      continue;
    }
    cleanElement(el);
    walk(el);
  }
}

/**
 * Document DÉTACHÉ servant d'aire de parsing.
 *
 * L'affectation `innerHTML` ci-dessous est la seule façon de faire analyser le
 * fragment par le parseur du navigateur — c'est le point de DÉPART de
 * l'assainissement, pas un rendu. Le document créé par `createHTMLDocument`
 * n'est jamais rattaché au document courant, et un `<script>` inséré via
 * `innerHTML` ne s'exécute jamais, même dans un document vivant. D'où la
 * dérogation ciblée à la règle anti-XSS, qui vise l'injection dans le DOM de
 * la page.
 */
function parseDetached(html: string): HTMLElement {
  const doc = document.implementation.createHTMLDocument('');
  // eslint-disable-next-line no-restricted-syntax -- document détaché, aire de parsing (cf. commentaire ci-dessus)
  doc.body.innerHTML = html;
  return doc.body;
}

/** HTML assaini, prêt pour `innerHTML`. Chaîne vide si l'entrée est vide. */
export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';
  const body = parseDetached(html);
  walk(body);
  return body.innerHTML;
}

/** True si le fragment porte du texte visible (et pas seulement des balises
 * vides comme le `<p><br></p>` que laisse un contenteditable vidé). */
export function hasVisibleContent(html: string | null | undefined): boolean {
  if (!html) return false;
  const text = parseDetached(html).textContent || '';
  return !!text.split(NBSP).join('').trim();
}

/** Marque de fin de bloc, remplacée par le séparateur demandé. Caractère de
 * contrôle échappé : il ne peut pas venir du texte saisi, la découpe est sûre. */
const BLOCK_MARK = '\u0001';

/**
 * Texte brut du fragment, espaces normalisés. Sert aux aperçus et aux
 * infobulles, où le HTML n'a pas sa place. Passe par le parseur plutôt que par
 * une regex de dépouillement : `<p>a</p><p>b</p>` doit donner « a b », pas
 * « ab », et un `&lt;` doit ressortir en `<`.
 *
 * `separator` sépare les blocs. Un `'\n'` reproduit les retours à la ligne de
 * l'auteur — ce qu'affiche l'attribut `title` d'une infobulle native, seule
 * mise en forme qu'elle sache rendre.
 */
export function plainTextFromRichText(html: string | null | undefined, separator = ' '): string {
  if (!html) return '';
  const body = parseDetached(html);
  // Élaguer d'abord : sans ça, `textContent` remonterait le CODE d'un
  // `<script>` comme s'il s'agissait du texte de l'ordre du jour.
  walk(body);
  // Les balises de bloc ne portent pas d'espace : sans cette marque, deux
  // paragraphes se recolleraient en un seul mot.
  for (const el of Array.from(body.querySelectorAll('p, li, br, ul, ol'))) {
    el.insertAdjacentText('afterend', BLOCK_MARK);
  }
  return (body.textContent || '')
    .split(NBSP)
    .join(' ')
    .split(BLOCK_MARK)
    .map((line) => line.split(/\s+/).filter(Boolean).join(' '))
    .filter(Boolean)
    .join(separator);
}
